import { Alert, LinearProgress, CircularProgress, TableContainer, Paper, Table, TableHead, TableCell, TableRow, TableBody, Chip, Tooltip, Button, FormControlLabel, Switch, Checkbox, IconButton, Menu, Snackbar, Grow, Typography, Link as MuiLink } from "@mui/material";
import { AccountCircle, Archive, ArrowDropDown, ArrowDropUp, Business, CheckCircle, DeleteForever, HighlightOff, Lock, MoreVert, NoteAdd, Public, SearchOff } from "@mui/icons-material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import config from "appConfig";
import APIRequest, { APIError, BuildAPIURL } from "system/APIRequest";
import { idToTitle, bytesToString, useQueryParam } from "system/Utilities";
import i18n from "translation";
import { PaginationBar } from "../PaginationBar";
import { ResourceActionButtonGroup } from "./ResourceActionButtonGroup";
import { TableFilterField, TableFilterGroup, TableFilterState } from "./TableFilter";

// Used for pretty printing relative date strings
dayjs.extend(relativeTime);

export interface FilterableResourceTableProps {
  endpoint: string;
  baseFilters?: Record<string, TableFilterState>;
  displayName?: string;
  asAdmin?: boolean;
  allowCreate?: boolean;
  visibleColumns?: string[];
}

export function FilterableResourceTable(props: FilterableResourceTableProps) {
  const navigate = useNavigate();
  const [serializedState, setSerializedState] = useQueryParam<any>("filters", {});

  // State defaults
  serializedState.perPage = serializedState.perPage || 20;
  serializedState.currentPage = serializedState.currentPage || 1;

  let [isLoading, setIsLoading] = useState<string | null>(null);
  let [data, setData] = useState<any | null>(null);
  const [endpoint, setEndpoint] = useState<string>(props.endpoint);
  const [fetchError, setFetchError] = useState<APIError | undefined>();
  const [checkedRows, setCheckedRows] = useState<number[]>([]);
  const [rowMenuAnchorEl, setRowMenuAnchorEl] = useState();
  const [activeRowMenuIdx, setActiveRowMenuIdx] = useState<number | undefined>();
  const [snackbarMessage, setSnackbarMessage] = useState<string | undefined>();
  const [downloadDisabled, setDownloadDisabled] = useState<boolean>(false);

  const displayName = props.displayName || idToTitle(endpoint);

  const buildURL = useCallback((format?: string): string => {
    let uri = 'obj/' + endpoint;

    // Build options into an object of key: value, which will be serialized as URL parameters
    let opts = {
      "schema": "1",
      "per_page": serializedState.perPage.toString(),
      "page": serializedState.currentPage.toString(),
      "minimal": format === "csv" ? "0" : "1"
    } as { [key: string]: string; }

    if (format) {
      opts["format"] = format;
    }

    // Add sort, if specified
    if (serializedState.sortBy && serializedState.sortDirection) {
      opts["sort"] = serializedState.sortBy;
      opts["sort_order"] = serializedState.sortDirection;
    }

    // Ask for deleted items, if specified
    if (serializedState.showRemoved) {
      opts["include_deleted"] = "1";
    }

    // Request as an admin if props asked us to do so
    // If the authenticated user isn't actually an admin, this has no effect
    if (props.asAdmin) {
      opts["admin"] = "1";
    }

    // Serialize filters, and add them if we have any
    // The API expects this as a JSON array in the "q" parameter
    if (serializedState.filterState || props.baseFilters) {
      let combinedFilters = Object.values(serializedState.filterState || {});

      if (props.baseFilters) {
        combinedFilters.push(...Object.values(props.baseFilters));
      }

      let serializedFilters = JSON.stringify(combinedFilters);
      if (serializedFilters !== "[]" && serializedFilters !== "") {
        opts["q"] = serializedFilters;
      }
    }

    if (serializedState.searchInput) {
      opts["search"] = serializedState.searchInput;
    }

    return BuildAPIURL(uri, opts);
  }, [endpoint, props.asAdmin, props.baseFilters, serializedState.currentPage, serializedState.filterState, serializedState.perPage, serializedState.searchInput, serializedState.showRemoved, serializedState.sortBy, serializedState.sortDirection]);

  // This callback is the data loading function
  // Any time a relevant state changes, such as the endpoint, page, etc., this will be called to reload the data
  const fetchData = useCallback(async () => {
    // This function is async, so when the request is fired, this state change will trigger a re-render
    //   showing a loading spinner while we wait
    setIsLoading(endpoint);

    // Uncheck everything on load, as the indexes will almost surely change
    setCheckedRows([]);
    setRowMenuAnchorEl(undefined);
    setActiveRowMenuIdx(undefined);

    const url = buildURL();

    // Fire off the request to load this data - this returns a promise we'll wait on
    await APIRequest(url, undefined, undefined, true).then((response: any) => {
      // Request succeeded; ensure the resource loaded matches what is currently selected
      // This may not be the case on a slow load if the user changes the resource while loading
      let loadingCurrentResource = true;
      setIsLoading((current: string | null): string | null => {
        loadingCurrentResource = current === response.endpoint;
        return loadingCurrentResource ? null : current;
      });

      if (loadingCurrentResource) {
        setData(response);
      } // else the resource was changed while this promise was running; in that case, ignore the load
    }).catch((reason: APIError) => {
      // Something went wrong! Set the error for display and remove the loading state
      setFetchError(reason);
      setIsLoading(null);
    });
  }, [buildURL, endpoint])


  // useEffect here will trigger a data load the first time this component renders
  // fetchData will be called each time filters and such change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // This callback is given to the TableFilter component, called when the Filter button is clicked with a non-empty filter state
  const handleFilter = useCallback((_search: string, _filterState: Record<string, TableFilterState>): void => {
    // This nastyness uses the setState callback to check if we're already loading
    // If we are, we don't want to change state and trigger another load
    // To achieve this, we return the passed in object to the callback if we're already loading,
    //   and remember that in the wasLoading local variable. If this is set, we won't set the filterState, which triggers a re-load
    let wasLoading = false;
    setIsLoading((current: string | null): string | null => {
      wasLoading = current !== null;
      if (current)
        return current;
      return endpoint;
    })

    if (!wasLoading) {
      // We use the expansion op here to get a new object reference
      // If this isn't done, this will only trigger a re-render the first time
      // Reset page, as filtering will likely change total results
      setSerializedState({ ...serializedState, searchInput: _search, filterState: _filterState, currentPage: 1 });
    }
  }, [endpoint, serializedState, setSerializedState]);

  // Callback used by the buttons in the header
  // Clicking these buttons cycles through sorting on that column
  const handleHeaderClick = (key: string): void => {
    if (serializedState.sortBy === key) {
      if (serializedState.sortDirection === "desc") {
        setSerializedState({ ...serializedState, sortDirection: "asc" });
      } else {
        // Sort was asc, set to null
        setSerializedState({ ...serializedState, sortDirection: null, sortBy: undefined });
      }
    } else {
      // New sort col
      setSerializedState({ ...serializedState, sortBy: key, sortDirection: "desc" });
    }
  }

  const handleActionDialogComplete = (success: boolean, resourceIds: string[], action: string, message?: APIError) => {
    if (success) {
      setSnackbarMessage(i18n.t("resource-action.dialog." + action + (resourceIds.length === 1 ? "-single" : "-template") + "-success").replace('${itemCount}', resourceIds.length.toString()));

      // Trigger a reload by just resetting serializedState's filterState to a new object copy of itself
      // We cannot just use serializedState, as our callback is not monitoring the root object
      serializedState.filterState = { ...(serializedState.filterState || {}) };
      setSerializedState(serializedState);

      // Clear the row actions menu if open
      setRowMenuAnchorEl(undefined);
      setActiveRowMenuIdx(undefined);
    }
  }


  // Done setting up state and callbacks
  if (props.endpoint !== endpoint) {
    // Resource was changed; clear sortBy, currentPage, filters, etc.
    setSerializedState({ currentPage: 1, perPage: 20 });
    setEndpoint(props.endpoint);
    setFetchError(undefined);
    setCheckedRows([]);
    setRowMenuAnchorEl(undefined);
    setActiveRowMenuIdx(undefined);

    // Clear data for this call to ensure we don't do a heavy re-render of the old stuff
    data = null;
    setData(null);

    // Trigger the loading message immediately
    isLoading = props.endpoint;
  }

  if (fetchError) {
    // A data fetch errored - display an error message
    return <Alert severity="error" style={{ marginTop: "2rem" }}>
      <h3>{i18n.t("admin.resources.load-error-header")}</h3>
      <p>{fetchError?.message || fetchError?.server_message} ({fetchError?.code})</p>
    </Alert>
  }

  if (data === null) {
    return <div style={{ padding: "1rem" }}>
      <LinearProgress />
      <p>{i18n.t("loading")}...</p>
    </div>;
  }

  // Hold a header, filterOptions and the table's content (tableRows)
  // These may not be set below if we have no data
  let tableHeader = [];
  let filterOptions: TableFilterField[] = [];
  let tableRows = Array<any>([]);

  const possibleBulkActions: ("unshare" | "duplicate" | "remove" | "restore" | "reassign" | "logout-sessions" | "view" | "edit")[] = ["view", "edit", "logout-sessions", "duplicate", "reassign", "unshare", "remove", "restore"];
  const possibleSingleActions: ("share" | "unshare" | "duplicate" | "create-from" | "remove" | "restore" | "reassign" | "logout-sessions" | "view" | "edit")[] = ["view", "edit", "logout-sessions", "share", "duplicate", "create-from", "reassign", "unshare", "remove", "restore"];

  if (data) {
    const bulkActionCount = data.permissions.filter(item => possibleBulkActions.includes(item)).length;
    // If we can perform bulk actions, we need a header for the actions column
    // The header is always present for the 3-dot actions button, but we only add the checkbox if we have more than 1 bulk actions (will always have at least read)
    tableHeader.push(<TableCell size="small" padding="checkbox" key={endpoint + "actions-header"} style={{ width: "3.5rem" }}>
      {bulkActionCount > 0 && <Tooltip title={String(checkedRows.length < data.output.length ? i18n.t("filterable-table.check-all") : i18n.t("filterable-table.uncheck-all"))}>
        <Checkbox checked={checkedRows.length >= data.output.length}
          indeterminate={checkedRows.length > 0 && checkedRows.length < data.output.length}
          onChange={
            (e: React.ChangeEvent<HTMLInputElement>) => {
              if (e.target.checked) {
                setCheckedRows([...Array(data.output.length).keys()]);
              } else setCheckedRows([]);
            }
          } />
      </Tooltip>}
    </TableCell>)

    if (serializedState.showRemoved) {
      tableHeader.push(<TableCell size="small" padding="checkbox" key={endpoint + "removed-col-hdr"}>
        <Tooltip title={i18n.t("filterable-table.mark-for-removed-header") as string}>
          { /* marginTop is used here to align the icon with the checkbox probably at its left */}
          <DeleteForever style={{ marginTop: "6px" }} />
        </Tooltip>
      </TableCell>)
    }
    
    const colKeys = props.visibleColumns ? props.visibleColumns : Object.keys(data.schema);

    // Build table header and filter options
    // Do this even if isLoading is set, as we want the header to show above a loading spinner
    // Iterate the returned schema, describing the columns in the data
    for (let keyIdx = 0; keyIdx < colKeys.length; keyIdx++){
      const key = colKeys[keyIdx];
      // Don't output the ID
      if (key === "id" || key.includes("_id"))
        continue;
      
      if (!Object.hasOwn(data.schema, key)) {
        console.error(key, 'missing from schema!');
        continue;
      }

      // Convert the key - the field's name - to a nice title
      // This converts something like "updated_on" to "Updated On"
      let title = idToTitle(key);

      // filterOptions - what we can filter by - is assembled here from the schema
      // We give each item a display title, the field's key, and the field's data type
      filterOptions.push({ title: title, id: key, type: data.schema[key].type, allows_null: data.schema[key].allows_null })

      // Is this column filtered?
      if (props.visibleColumns && props.visibleColumns.findIndex(v => v === key) === -1) {
        continue;
      }

      // We don't support rendering multi-value columns yet
      if (data?.schema[key]?.multi_value) {
        continue;
      }

      // We don't want to render cache entries
      if (key === "cache") {
        continue;
      }

      // Don't render JSON data here
      if (data.schema[key].json) {
        continue;
      }
      
      // Skip fields with their data omitted (due to size)
      if (data.missing_field_names && data.missing_field_names.includes(key)) {
        continue;
      }

      // Only allow sorting if the field is indexed, OR the result count is < 1M rows (for numbers) or 100k (for text)
      const isSortable = data?.schema[key]?.indexed || data?.pagination?.total < (data?.schema?.[key]?.type === "string" ? 100000 : 1000000);

      // The table header is an array of cells, one per column
      // We display the field's name as a clickable Button
      // Clicking the Button will alternate between sorting modes on that column
      tableHeader.push(
        <TableCell size="small" key={endpoint + title}>
          {isSortable &&
            <Tooltip title={i18n.t("filterable-table.sort-by") + ' ' + title}>
              <span>
                <Button onClick={() => handleHeaderClick(key)}
                  disabled={isLoading !== null} >
                  {title}
                  {serializedState.sortBy === key && serializedState.sortDirection === "asc" && <ArrowDropUp />}
                  {serializedState.sortBy === key && serializedState.sortDirection === "desc" && <ArrowDropDown />}
                </Button>
              </span>
            </Tooltip>}
          {!isSortable && <Typography>{title}</Typography>}
        </TableCell>);
    }

    if (isLoading === null) {
      data.output.forEach((row: any, idx: number) => {
        let cols: Array<any> = [];

        // If we have bulk actions available, show checkboxes to select multiple rows
        // Always show a 3-dot menu for row actions, as we will at least have "view"
        // Test for more than 1 permission to show checks, as it will always have at least "read"
        cols.push(<TableCell size="small" padding="none" key={endpoint + "action-col-" + idx.toString()}>
          <div style={{ width: "4rem", display: "flex", alignItems: "center", justifyContent: "space-around" }}>
            {bulkActionCount > 0 && <Checkbox checked={checkedRows.includes(idx)} onClick={
              (e: React.MouseEvent<HTMLButtonElement>) => {
                if (checkedRows.includes(idx)) {
                  setCheckedRows(checkedRows.filter((v: number) => v !== idx));
                } else {
                  if (e.shiftKey && checkedRows.length > 0) {
                    // Check from the last checked row to here
                    // This gives us the maximum checked index less than the clicked idx, or 0 if checkedRows is empty
                    let minChecked = data.output.length;
                    let maxChecked = -1;

                    for (const checked of checkedRows) {
                      minChecked = checked < minChecked ? checked : minChecked;
                      maxChecked = checked > maxChecked ? checked : maxChecked;
                    }

                    if (maxChecked < idx) {
                      // Check rows from maxChecked to the clicked index
                      for (let i: number = maxChecked; i <= idx; i++) {
                        checkedRows.push(i);
                      }
                      // And set this array using a new reference
                      setCheckedRows([...checkedRows]);
                    } else {
                      // They shift-clicked ABOVE the last-checked row
                      for (let i: number = idx; i < minChecked; i++) {
                        checkedRows.push(i);
                      }
                      setCheckedRows([...checkedRows]);
                    }
                  } else {
                    setCheckedRows([idx, ...checkedRows]);
                  }
                }
              }} />}

            <IconButton
              size="small"
              key={endpoint + "row-menu-button-" + idx.toString()}
              style={{ width: "1rem", height: "1.5rem" }}

              onClick={(event: any): void => {
                setRowMenuAnchorEl(event?.currentTarget);
                setActiveRowMenuIdx(idx);
              }} >
              <MoreVert />
            </IconButton>

            {/* IconButton above opens this menu to perform actions with this row */}
            <Menu
              key={endpoint + "row-menu-" + idx.toString()}
              anchorEl={rowMenuAnchorEl}
              open={Boolean(rowMenuAnchorEl) && activeRowMenuIdx === idx}
              onClose={() => setRowMenuAnchorEl(undefined)} >

              <ResourceActionButtonGroup
                resourcePath={`/admin/resources/${endpoint}`}
                actions={possibleSingleActions.filter((v) => {
                  // Permissions never includes "view" or "edit", as those permissions are "read" and "update"
                  if (v === "edit" && data.permissions.includes("update"))
                    return true;
                  if (v === "view" && !data.permissions.includes("update"))
                    return true;
                  if (v === "create-from" && data.permissions.includes("create-from"))
                    return true;

                  if (!data.permissions.includes(v))
                    return false;
                  if (v === "remove")
                    return row._deleted !== true;
                  if (v === "restore")
                    return row._deleted === true;
                  if (v === "unshare")
                    return row._is_shared === true;
                  return true;
                })}
                endpoint={endpoint}
                resourceIds={data.output[idx].id}
                handleActionComplete={handleActionDialogComplete}
                asAdmin={props.asAdmin}
                showSharedWarning={data.output[idx]._is_shared}
                defaultAccountId={data?.output[idx]?._account?.id}
                removeAs={["user", "account"].includes(endpoint) ? "deactivate" : "remove"}
                restoreAs={["user", "account"].includes(endpoint) ? "reactivate" : "restore"}
                onDialogClose={(resourceIds: string[], action: string) => { setRowMenuAnchorEl(undefined); setActiveRowMenuIdx(undefined); }}
                accountAccess={data.output[idx]?.access}
                reassignable={data.permissions.includes("reassign")}
                asMenuItems />
            </Menu>
          </div>
        </TableCell>)

        if (serializedState.showRemoved) {
          cols.push(<TableCell size="small" padding="checkbox" key={endpoint + "removed-col-" + idx.toString()}>
            {row?._deleted && <DeleteForever color="error" />}
          </TableCell>)
        }

        for (let keyIdx = 0; keyIdx < colKeys.length; keyIdx++){
          const key = colKeys[keyIdx];
          if (key === "id" || key.includes("_id"))
            continue;

          // Is this column filtered?
          if (props.visibleColumns && props.visibleColumns.findIndex(v => v === key) === -1)
            continue;

          // We don't support rendering multi-value columns yet
          if (data.schema[key].multi_value)
            continue;

          // We don't want to render cache entries
          if (key === "cache")
            continue;

          // Don't render JSON data here
          if (data.schema[key].json)
            continue;
          
          // Skip fields with their data omitted (due to size)
          if (data.missing_field_names && data.missing_field_names.includes(key))
            continue;

          let cellContent: JSX.Element;

          // Output the display value for a cell
          // First, we will check for special cases: null and objects, which we handle differently
          if (row[key] === null || row[key] === undefined || row[key] === "") {
            // Display null or empty string as "null" in a grey Chip
            cellContent = <Chip size="small" label="null" />
          } else if (key === "_user") {
            // _user is a user object - display the email in a Chip
            cellContent = <Link style={{ color: 'inherit', textDecoration: 'inherit' }} to={"/admin/resources/user/" + row[key].id}>
              <Chip size="small" icon={<AccountCircle />} label={row[key].email} color={row[key].active ? "info" : "default"} />
            </Link>
          } else if (key === "_account") {
            // _account is an account object - display the account's name in a Chip
            cellContent = <Link style={{ color: 'inherit', textDecoration: 'inherit' }} to={"/admin/resources/account/" + row[key].id}>
              <Chip size="small" icon={<Business />} label={row[key].name} color={row[key].active ? "success" : "default"} />
            </Link>
          } else if (key === "access" && ["restricted", "account-read", "account-modify", "public"].includes(row[key])) {
            // Account-level access permissions, shown as an icon
            if (row[key] === "restricted") {
              cellContent = <Lock color="error" />;
            } else if (row[key] === "account-read") {
              cellContent = <Business color="warning" />;
            } else if (row[key] === "account-modify") {
              cellContent = <Business color="success" />;
            } else if (row[key] === "public") {
              cellContent = <Public color="info" />
            } else {
              // Shouldn't happen, given the above check, but we'll get a build error without this
              cellContent = <Chip size="small" label="null" />
            }

            cellContent = <Tooltip title={<span>{i18n.t("resource-action.dialog.share-account-access-desc-" + row[key])}</span>}>
              {cellContent}
            </Tooltip>
          } else if (key === "hubspot_company" && row[key]) {
            // This is a hubspot ID, link to Hubspot
            cellContent = <Link style={{ color: 'inherit', textDecoration: 'inherit' }} target="_blank" to={"https://app.hubspot.com/contacts/1716283/company/" + row[key]}>
              <Chip size="small" icon={<Business />} label={row[key]} color={"warning"} />
            </Link>
          } else {
            // Here, we know the value is non-null and not an object
            if (data.schema[key]?.type === "boolean") {
              // Display boolean as a checked-or-empty circle, depending on state
              cellContent = row[key] ? <CheckCircle /> : <HighlightOff />
            } else if (data.schema[key]?.type === "datetime") {
              // Datetimes are displayed as a local-formatted date string (e.g. April 5, 1979 👋)
              //   with the full local-formatted date & time string, and relative distance, in a tooltip
              let _d = new Date(row[key]);
              cellContent = <Tooltip title={<span>{_d.toLocaleDateString(undefined, config().dateTimeFormatOptions)}<br />{dayjs(_d).fromNow()}</span>}>
                <span>{_d.toLocaleDateString(undefined, config().dateFormatOptions)}</span>
              </Tooltip>
            } else if (data.schema[key]?.type === "date") {
              // Dates are displayed as UTC dates without times (e.g. April 5, 1979 👋)
              //   with the distance in a tooltip - this should use new Date(row[key]), to match distance to new Date()
              let _d = new Date(row[key]);
              _d = new Date(_d.getUTCFullYear(), _d.getUTCMonth(), _d.getUTCDate(), 0, 0, 0);
              cellContent = <Tooltip title={<span>{_d.toLocaleDateString(undefined, config().dateFormatOptions)}<br />{dayjs(row[key]).fromNow()}</span>}>
                <span>{_d.toLocaleDateString(undefined, config().dateFormatOptions)}</span>
              </Tooltip>
            } else if (data.schema[key]?.type === "string") {
              // Strings are just displayed plainly, unless they are too long (over 32 chars)
              //   in the too-long case, we truncate, indicate such with a Unicode elipses, 
              //   and display the full text string in a tooltip
              const displayVal:string = typeof(row[key]) === "string" || row[key] instanceof String ? row[key] : JSON.stringify(row[key]);

              if (displayVal.length > 32) {
                // String too long - truncate and display the full string in a tooltip
                let val = displayVal.substring(0, 32) + '…';
                cellContent = <Tooltip title={displayVal}>
                  <span>{val}</span>
                </Tooltip>
              } else {
                // Plain string, just display as-is
                cellContent = <span>{displayVal}</span>
              }
            } else {
              // Simple value - just display as-is
              cellContent = <span>{typeof row[key] === 'number' ? (key.includes('bytes') || key.includes('storage') ? bytesToString(row[key]) : row[key].toLocaleString()) : row[key]}</span>
            }
          }

          // If not prefixed with _ (_user, _account), wrap in a Link to the resource editor
          // We don't wrap link on _ prefix, as those have their own Links!
          if (!key.startsWith("_") && key !== 'hubspot_company') {
            const link_to = endpoint === 'billing-order' ? `/admin/billing/order/${row["id"]}` :
              endpoint === 'billing-invoice' ? `/admin/billing/invoice/${row["id"]}` :
              `/admin/resources/${endpoint}/${row["id"]}`;
            cellContent = <Link to={link_to} style={{ color: 'inherit', textDecoration: 'inherit' }}>
              {cellContent}
            </Link>
          }

          cols.push(<TableCell key={endpoint + idx.toString() + key} size="small">
            {cellContent}
          </TableCell>);
        }

        // Add a row with our populated columns of TableCell elements
        tableRows.push(
          <TableRow key={endpoint + "row" + idx.toString()} hover selected={checkedRows.includes(idx)}>
            {cols}
          </TableRow >);
      });
    }
  }

  // Set the max width of the table to 200 px per col
  let maxTableWidth = (tableHeader.length * 200).toString() + "px";

  let totalDeleted = 0;
  let totalShared = 0;
  let actionIds: string[] = [];
  let accountAccess = checkedRows.length > 0 ? data.output[checkedRows[0]]?.access : undefined;
  checkedRows.forEach((v: number) => {
    if (data.output[v]._deleted)
      totalDeleted++;
    if (data.output[v]._is_shared)
      totalShared++;
    actionIds.push(data.output[v].id);
    if(data.output[v].access !== accountAccess)
      accountAccess = undefined;
  });

  // Render our now-assembled table
  return (
    <Paper sx={{ textAlign: "left", width: "100%" }}>
      <Typography variant="h3" color="primary" sx={{
        padding: 1
      }}>{displayName}</Typography>
      {data?.permissions?.includes("ui_create") && (props.allowCreate === undefined || props.allowCreate === true) &&
        <Button
          variant="contained"
          color="success"
          startIcon={<NoteAdd />}
          style={{ margin: "10px" }}
          onClick={() => navigate(`/admin/resources/${endpoint}/:create`)}>
          {i18n.t("new") + " " + displayName}
        </Button>}

      {data?.endpoint === "billing-order" && (props.allowCreate === undefined || props.allowCreate === true) &&
        <Button
          variant="contained"
          color="success"
          startIcon={<NoteAdd />}
          style={{ margin: "10px" }}
          onClick={() => navigate(`/admin/billing/new-customer`)}>
          {"New Account Order"}
        </Button>}

      <Paper>
        {data?.permissions?.includes("include_deleted") &&
          <div style={{ float: "right", marginRight: "1rem", marginTop: "0.5rem" }}>
            <FormControlLabel control={
              <Switch 
                size="small"
                color="error"
                checked={serializedState.showRemoved}
                onChange={
                  (e: React.ChangeEvent<HTMLInputElement>) => setSerializedState({ ...serializedState, showRemoved: e.target.checked })
                } />
              }
              label={
                <Typography variant="subtitle2">
                  {i18n.t("filterable-table.show-removed")}
                </Typography>} 
              labelPlacement="start" />
          </div>
        }

        <div style={{ paddingLeft: "1rem", paddingTop: "0" }}>
          <TableFilterGroup filterOptions={filterOptions} initialSearch={serializedState.searchInput} initialState={serializedState.filterState} onFilter={handleFilter} />
        </div>

        {data.permissions.length > 0 &&
          // Rows are checked, and we can take bulk action on them - present options
          <Grow in={checkedRows.length > 0}>
            <div style={{ paddingLeft: "1rem", paddingTop: "1rem", display: "flex", gap: "0.5rem" }}>
              <ResourceActionButtonGroup
                resourcePath={`/admin/resources/${endpoint}`}
                label={i18n.t("filterable-table.with-checked") + ":"}
                actions={possibleBulkActions.filter((v) => {
                  // Filter available actions based on resource permissions and what is checked
                  if (v === "create-from" && checkedRows.length === 1 && data.permissions.includes("create-from"))
                    return true;
                  if (!data.permissions.includes(v))
                    return false;
                  if (v === "remove")
                    return totalDeleted === 0;
                  if (v === "restore")
                    return totalDeleted === checkedRows.length && totalDeleted > 0;
                  if (v === "unshare")
                    return totalShared === checkedRows.length && checkedRows.length > 0;
                  return true;
                })}
                endpoint={endpoint}
                resourceIds={checkedRows.map((idx) => data.output[idx].id)}
                handleActionComplete={handleActionDialogComplete}
                asAdmin={props.asAdmin}
                removeAs={["user", "account"].includes(endpoint) ? "deactivate" : "remove"}
                restoreAs={["user", "account"].includes(endpoint) ? "reactivate" : "restore"}
                accountAccess={accountAccess}
                reassignable={data.permissions.includes("reassign")}
                showSharedWarning={totalShared > 0} />
            </div>
          </Grow>
        }

        {(data.output || []).length === 0 &&
          <div style={{ padding: "1rem", width: "100%", textAlign: "center" }}>
            <SearchOff fontSize="large" />
            <p>{i18n.t("filterable-table.empty-data")}</p>
          </div>
        }

        <TableContainer style={{ maxWidth: maxTableWidth, marginLeft: "auto", marginRight: "auto", overflow: "scroll" }}>
          {(data.output || []).length > 0 &&
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {tableHeader}
                </TableRow>
              </TableHead>
              <TableBody>

                {isLoading !== null &&
                  <TableRow>
                    <TableCell key={endpoint + "loadingCell"}>
                      <CircularProgress />
                      <p>{i18n.t("loading")}...</p>
                    </TableCell>
                  </TableRow>}

                {tableRows}

              </TableBody>
            </Table>
          }

        </TableContainer>

        <PaginationBar
          totalItems={parseInt(data.pagination.total)}
          perPage={serializedState.perPage}
          perPageOptions={[20, 50, 100, 500, 1000, 5000, 10000]}
          currentPage={serializedState.currentPage}
          onChange={(currentPage: number, perPage: number): void => {
            setSerializedState({ ...serializedState, perPage: perPage, currentPage: currentPage });
          }}
        />

        <Tooltip title={i18n.t("download")}>
          <Button disabled={downloadDisabled}>
            <MuiLink onClick={() => {
              if(downloadDisabled)
                return;
              setDownloadDisabled(true);

              APIRequest(buildURL("csv")).then((response: Response) => {
                return response.arrayBuffer();
              }).then((data: ArrayBuffer) => {
                // create a blob url representing the data
                const blob = new Blob([data]);
                const url = window.URL.createObjectURL(blob);
                // attach blob url to anchor element with download attribute
                const anchor = document.createElement('a');
                anchor.setAttribute('href', url);
                anchor.setAttribute('download', displayName + ".csv");
                anchor.click();

                // Clean up
                window.URL.revokeObjectURL(url);
                setDownloadDisabled(false);
              });
            }}>
            
              <Archive color={downloadDisabled ? "disabled" : "primary"} />
            </MuiLink>
          </Button>
        </Tooltip>
        
      </Paper>

      {
        snackbarMessage !== undefined &&
        <Snackbar open={snackbarMessage !== undefined}
          autoHideDuration={6000}
          onClose={(e: any, reason: any) => {
            if (reason === "clickaway")
              return;
            setSnackbarMessage(undefined);
          }} >
          <Alert
            onClose={(e: any) => setSnackbarMessage(undefined)}
            severity="success"
            sx={{ width: '100%' }} >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      }
    </Paper >
  );
}